import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip, Text } from '@hotjar/hotsauce';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { getLimitsByFeatureGroup } from '@hotjar/webapp/domains/common/feature';
import { localeNumber } from '@hotjar/insights-common/lib/number';

import type {
  AnalyticsProduct,
  FeedbackProduct,
  Product,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { isAnalyticsProduct } from '@hotjar/webapp/domains/revenue/hooks/usePlanAllowances';

import * as S from './styles';

interface Props<T> {
  tier: FeatureGroup;
  tierProducts: T;
  productType: ProductType;
  selectedPlan: Product;
  className?: string;
}

export const PlanFeatures = <T extends (AnalyticsProduct | FeedbackProduct)[]>({
  tier,
  tierProducts,
  productType,
  selectedPlan,
  className,
}: Props<T>): JSX.Element => {
  const { t } = useTranslation('revenue');

  if (!tierProducts.length) {
    throw new Error('No tierProducts defined');
  }

  const minAllowance = useMemo(
    () =>
      Math.min(
        ...tierProducts.map((product) =>
          product.type === ProductType.Analytics
            ? product.dailyCcSessions
            : product.monthlyResponses,
        ),
      ),
    [tierProducts],
  );
  const maxAllowance = useMemo(
    () =>
      Math.max(
        ...tierProducts.map((product) =>
          product.type === ProductType.Analytics
            ? product.dailyCcSessions
            : product.monthlyResponses,
        ),
      ),
    [tierProducts],
  );

  const featuresPerFeatureGroup: Record<
    ProductType,
    {
      title?: string;
      features: string[][];
    }
  > = useMemo(() => {
    const features = {
      [ProductType.Analytics]: {
        title: t(`plan-comparison-modal.features.title.${tier}`),
        features: t(`plan-comparison-modal.features.multi-sku-list.${productType}.${tier}`, {
          returnObjects: true,
          noOfSessions: localeNumber(minAllowance),
          noOfRecordings: localeNumber(minAllowance * 30),
          noOfSessionsMax: localeNumber(maxAllowance),
          noOfSessionsMin: localeNumber(minAllowance),
        }) as Array<Array<string>>,
      },
      [ProductType.Feedback]: {
        title: t(`plan-comparison-modal.features.title.${tier}`),
        features: t(`plan-comparison-modal.features.multi-sku-list.${productType}.${tier}`, {
          returnObjects: true,
          noOfResponses: localeNumber(minAllowance),
          // multiply the ccDailyAllowance by 30 so we get the *monthly* daily allowance
          noOfRecordings: localeNumber(minAllowance * 30),
          noOfSurveys: localeNumber(getLimitsByFeatureGroup('surveys', tier)),
          noOfManualHeatmaps: localeNumber(getLimitsByFeatureGroup('heatmaps', tier)),
          noOfResponsesMax: localeNumber(maxAllowance),
          noOfResponsesMin: localeNumber(minAllowance),
        }) as Array<Array<string>>,
      },
    } as const;

    if (
      isAnalyticsProduct(selectedPlan) &&
      selectedPlan.dailyCcSessions >= 4000 &&
      Array.isArray(features[ProductType.Analytics].features)
    ) {
      features[ProductType.Analytics].features.unshift(
        t('plan-comparison-modal.features.csm', {
          returnObjects: true,
        }),
      );
      features[ProductType.Analytics].features.splice(
        features[ProductType.Analytics].features.length - 1,
        0,
        [t('plan-comparison-modal.features.priority-support')],
      );
    }

    return features;
  }, [t, tier, productType, minAllowance, maxAllowance, selectedPlan]);

  return (
    <S.FeaturesContainer
      data-testid={`plan-comparison-${tier}-features-list`}
      className={className}
    >
      {featuresPerFeatureGroup[productType].title && (
        <S.ListTitle>{featuresPerFeatureGroup[productType].title}</S.ListTitle>
      )}
      {featuresPerFeatureGroup[productType]?.features?.map &&
        featuresPerFeatureGroup[productType].features.map(([feature, description]) => (
          <S.FeatureListItem key={feature}>
            <Text textType="body2">{feature}</Text>
            {!!description && (
              <Tooltip text={description}>
                <S.StyledIcon icon="info" color="text_default" />
              </Tooltip>
            )}
          </S.FeatureListItem>
        ))}
    </S.FeaturesContainer>
  );
};
